import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Engine } from './core/Engine';
import { GameLoop } from './core/GameLoop';
import { AssetLoader } from './loading/AssetLoader';
import { LoadingScreen } from './loading/LoadingScreen';
import { getManifestByPhase } from './loading/AssetManifest';
import { WorldManager } from './world/WorldManager';
import { CharacterController } from './character/CharacterController';
import { AnimationStateMachine } from './character/AnimationStateMachine';
import { ThirdPersonCamera } from './character/ThirdPersonCamera';
import { VehicleController } from './character/VehicleController';
import { InputManager } from './input/InputManager';
import { MOBILE_FPS, DESKTOP_FPS, KENNEY_SCALE, TILE_SIZE } from './config';
import { isMobile } from './utils/platform';
import { tileToWorld } from './world/CityMap';
import { MissionManager } from './missions/MissionManager';
import { Marker } from './missions/Marker';
import { Compass } from './missions/Compass';
import { MissionHUD } from './missions/MissionHUD';
import { ShiftSummary } from './missions/ShiftSummary';
import { NpcPassenger } from './missions/NpcPassenger';
import type { CarInstance } from './world/CarTraffic';

async function main() {
  const engine = new Engine();
  const loader = new AssetLoader();
  const loadingScreen = new LoadingScreen();
  const input = new InputManager();
  const fps = isMobile() ? MOBILE_FPS : DESKTOP_FPS;

  // ─── Phase 1: Load roads + character (blocking) ───
  loadingScreen.show();
  loader.onProgress = (p) => loadingScreen.update(p);

  console.time('phase1-download');
  const phase1 = getManifestByPhase(1);
  await loader.loadPhase(phase1, 1);
  console.timeEnd('phase1-download');

  // ─── Build world ───
  const world = new WorldManager(engine.scene, loader);
  console.time('build-roads');
  world.buildRoads();
  console.timeEnd('build-roads');

  // ─── Load character ───
  const charGltf = loader.getOriginal('character/Meshy_AI_Meshy_Merged_Animations');
  if (!charGltf) {
    console.error('Character model failed to load!');
    return;
  }

  // Use SkeletonUtils.clone to properly clone SkinnedMesh + skeleton
  const charModel = cloneSkeleton(charGltf.scene) as THREE.Group;
  // Character scale: mini-chars are ~1u tall natively, scale to ~1.5u (proportional to 4u buildings)
  charModel.scale.setScalar(KENNEY_SCALE * 0.2);
  charModel.traverse((c: any) => {
    if (c.isMesh || c.isSkinnedMesh) {
      c.castShadow = true;
      c.receiveShadow = true;
      c.frustumCulled = false;
    }
  });
  engine.scene.add(charModel);

  const charController = new CharacterController(charModel, world.collisionWorld);
  const animState = new AnimationStateMachine(charModel, charGltf.animations);
  charController.setAnimations(animState);

  // Spawn on a center crossroad — tile (18, 18) for GRID_SIZE=37
  const [spawnX, spawnZ] = tileToWorld(18, 18, TILE_SIZE);
  charController.position.set(spawnX, 0, spawnZ);
  charModel.position.set(spawnX, 0, spawnZ);

  // Debug: expose globals for console inspection
  (window as any).__debug = { engine, charModel, charController, world, tpCamera: null as any };

  // Log character bounding box for scale calibration
  const charBox = new THREE.Box3().setFromObject(charModel);
  const charSize = new THREE.Vector3();
  charBox.getSize(charSize);
  console.log('Character bounding box:', {
    size: { x: charSize.x.toFixed(2), y: charSize.y.toFixed(2), z: charSize.z.toFixed(2) },
    min: { x: charBox.min.x.toFixed(2), y: charBox.min.y.toFixed(2), z: charBox.min.z.toFixed(2) },
    max: { x: charBox.max.x.toFixed(2), y: charBox.max.y.toFixed(2), z: charBox.max.z.toFixed(2) },
    position: { x: charModel.position.x.toFixed(2), y: charModel.position.y.toFixed(2), z: charModel.position.z.toFixed(2) },
    scale: charModel.scale.x,
  });

  // ─── Camera: ThirdPersonCamera (character mode) ───
  const tpCamera = new ThirdPersonCamera(engine.camera, world.collisionWorld);
  (window as any).__debug.tpCamera = tpCamera;

  // ─── Camera: OrbitControls (camera mode) ───
  const orbitControls = new OrbitControls(engine.camera, engine.renderer.domElement);
  orbitControls.enableDamping = true;
  orbitControls.dampingFactor = 0.1;
  orbitControls.enabled = false; // Start in character mode

  // ─── Mode indicator UI ───
  const modeIndicator = document.getElementById('mode-indicator');

  input.setModeChangeCallback((mode) => {
    if (mode === 'camera') {
      // Switch to orbit: position orbit target at character
      orbitControls.target.copy(charController.position);
      orbitControls.enabled = true;
      if (modeIndicator) {
        modeIndicator.textContent = 'CAMERA MODE';
        const hint = document.createElement('span');
        hint.className = 'hint';
        hint.textContent = 'TAB to toggle';
        modeIndicator.appendChild(hint);
        modeIndicator.classList.add('camera-mode');
      }
    } else {
      orbitControls.enabled = false;
      if (modeIndicator) {
        modeIndicator.textContent = 'CHARACTER MODE';
        const hint = document.createElement('span');
        hint.className = 'hint';
        hint.textContent = 'TAB to toggle';
        modeIndicator.appendChild(hint);
        modeIndicator.classList.remove('camera-mode');
      }
    }
  });

  // ─── Mission system ───
  const missionManager = new MissionManager();
  const pickupMarker = new Marker();
  const dropoffMarker = new Marker();
  dropoffMarker.setColor(0x33ccff);
  engine.scene.add(pickupMarker.object);
  engine.scene.add(dropoffMarker.object);
  const compass = new Compass();
  // One NPC waving at the pickup, another greeting at the drop-off. Each
  // picks a fresh Kenney mini-character at random per spawn. Silent no-op
  // until Phase 3 streams in the character assets.
  const pickupNpc = new NpcPassenger(engine.scene, loader);
  const dropoffNpc = new NpcPassenger(engine.scene, loader);

  let vehicle: VehicleController | null = null;
  let currentCar: CarInstance | null = null;
  const ENTER_CAR_RADIUS = 3;
  const EXIT_SIDE_OFFSETS = [
    { sign: 1, radius: 2.2 },
    { sign: -1, radius: 2.2 },
    { sign: 1, radius: 3.2 },
    { sign: -1, radius: 3.2 },
  ];

  let highlightedTaxis: CarInstance[] = [];
  const clearTaxiHighlights = () => {
    for (const car of highlightedTaxis) world.traffic.applyTaxiHighlight(car, false);
    highlightedTaxis = [];
  };

  const shiftSummary = new ShiftSummary({
    onStartNew: () => {
      shiftSummary.hide();
      pickupMarker.setVisible(false);
      dropoffMarker.setVisible(false);
    },
  });

  const enterVehicle = () => {
    const near = world.traffic.findNearestCar(
      charController.position.x,
      charController.position.z,
      ENTER_CAR_RADIUS,
    );
    if (!near) return;
    world.traffic.markAsParked(near);
    currentCar = near;
    vehicle = new VehicleController(near.model, world.collisionWorld);
    vehicle.onCrash = () => missionManager.onCrash();
    charModel.visible = false;
    clearTaxiHighlights();
    if (near.carName === 'taxi' && missionManager.state === 'idle') {
      missionManager.startShift();
    }
  };

  const exitVehicle = () => {
    if (!vehicle || !currentCar) return;
    const carX = vehicle.position.x;
    const carZ = vehicle.position.z;
    const forwardX = Math.sin(vehicle.heading);
    const forwardZ = Math.cos(vehicle.heading);
    const rightX = forwardZ;
    const rightZ = -forwardX;
    for (const { sign, radius } of EXIT_SIDE_OFFSETS) {
      const tx = carX + rightX * sign * radius;
      const tz = carZ + rightZ * sign * radius;
      const resolved = world.collisionWorld.resolveCollision(tx, 0.5, tz, 0.5);
      if (resolved.x === tx && resolved.z === tz) {
        charController.position.set(tx, 0, tz);
        charModel.position.set(tx, 0, tz);
        break;
      }
    }
    world.traffic.releaseTaxi(currentCar);
    currentCar = null;
    vehicle = null;
    charModel.visible = true;
    if (missionManager.state === 'awaiting_pickup' || missionManager.state === 'en_route') {
      missionManager.abandonRide();
    }
  };

  // HUD holds its own references through event subscriptions and DOM; no local needed.
  new MissionHUD(missionManager, {
    onEndShiftClick: () => {
      if (missionManager.state === 'idle') return;
      const stats = missionManager.endShift();
      pickupMarker.setVisible(false);
      dropoffMarker.setVisible(false);
      shiftSummary.show(stats);
      if (vehicle) exitVehicle();
    },
  });

  missionManager.on('stateChanged', (s) => {
    if (s.state === 'awaiting_pickup' && s.pickupTarget) {
      pickupMarker.setPosition(s.pickupTarget.curbX, s.pickupTarget.curbZ);
      pickupMarker.setColor(0xffcc33);
      pickupMarker.setVisible(true);
      dropoffMarker.setVisible(false);
      pickupNpc.show(s.pickupTarget);
      dropoffNpc.hide();
    } else if (s.state === 'en_route' && s.dropoffTarget) {
      pickupMarker.setVisible(false);
      dropoffMarker.setPosition(s.dropoffTarget.curbX, s.dropoffTarget.curbZ);
      dropoffMarker.setVisible(true);
      pickupNpc.hide(); // picked up — the fare is "in the car"
      dropoffNpc.show(s.dropoffTarget); // different character waiting to be dropped off
    } else if (s.state === 'result_card' || s.state === 'idle') {
      pickupMarker.setVisible(false);
      dropoffMarker.setVisible(false);
      pickupNpc.hide();
      dropoffNpc.hide();
    }
  });

  // ─── Game Loop ───
  const loop = new GameLoop(
    (delta) => {
      const inp = input.getInput();

      if (input.wasEnterPressed() && missionManager.state !== 'result_card') {
        if (!vehicle) enterVehicle();
        else exitVehicle();
      }

      if (vehicle) {
        vehicle.update(delta, inp);
        tpCamera.rotate(inp.cameraRotateX, inp.cameraRotateY);
        const isMovingCar = Math.abs(vehicle.forwardSpeed) > 0.5;
        tpCamera.update(delta, vehicle.position, isMovingCar ? vehicle.heading : undefined);
      } else if (input.mode === 'character') {
        tpCamera.rotate(inp.cameraRotateX, inp.cameraRotateY);
        charController.update(delta, inp, tpCamera.getYaw());
        const isMoving = inp.moveX !== 0 || inp.moveZ !== 0;
        tpCamera.update(
          delta,
          charController.position,
          isMoving ? charController.model.rotation.y : undefined,
        );
      } else {
        orbitControls.update();
      }

      const focusX = vehicle ? vehicle.position.x : charController.position.x;
      const focusZ = vehicle ? vehicle.position.z : charController.position.z;
      world.update(delta, focusX, focusZ);

      missionManager.update(delta, focusX, focusZ);
      pickupMarker.update(delta);
      dropoffMarker.update(delta);
      pickupNpc.update(delta);
      dropoffNpc.update(delta);

      const snap = missionManager.snapshot();
      const targetTile =
        snap.state === 'awaiting_pickup' ? snap.pickupTarget :
        snap.state === 'en_route' ? snap.dropoffTarget : null;
      const compassTarget = targetTile ? { x: targetTile.curbX, z: targetTile.curbZ } : null;
      const compassKind = snap.state === 'en_route' ? 'dropoff' : 'pickup';
      compass.update(focusX, focusZ, compassTarget, tpCamera.getYaw(), compassKind);

      if (!vehicle && missionManager.state === 'idle') {
        const nearest = world.traffic.findNearestTaxis(
          charController.position.x,
          charController.position.z,
          3,
        );
        const nextSet = new Set(nearest);
        highlightedTaxis = highlightedTaxis.filter((car) => {
          if (!nextSet.has(car)) {
            world.traffic.applyTaxiHighlight(car, false);
            return false;
          }
          return true;
        });
        for (const car of nearest) {
          if (!highlightedTaxis.includes(car)) {
            world.traffic.applyTaxiHighlight(car, true);
            highlightedTaxis.push(car);
          }
        }
      } else if (highlightedTaxis.length) {
        clearTaxiHighlights();
      }

      // Drive the on-screen ENTER/EXIT button (mobile only). Highlighted when
      // the player is in a car (EXIT) or close enough to enter one.
      if (input.mobile) {
        if (vehicle) {
          input.setTouchActionState('Exit', true, false);
        } else {
          const near = world.traffic.findNearestCar(
            charController.position.x,
            charController.position.z,
            ENTER_CAR_RADIUS,
          );
          input.setTouchActionState('Enter', near != null, near == null);
        }
      }
    },
    () => engine.render(),
    fps
  );

  loop.start();

  // ─── Phase 2: Buildings — keeps loading screen up until placed ───
  (async () => {
    console.time('phase2-download');
    const phase2 = getManifestByPhase(2);
    await loader.loadPhase(phase2, 2);
    console.timeEnd('phase2-download');

    console.time('build-buildings');
    await world.buildBuildings();
    console.timeEnd('build-buildings');

    loadingScreen.hide();

    // Phases 3→4 stream in after the screen is down
    const phase3 = getManifestByPhase(3);
    await loader.loadPhase(phase3, 3);
    world.buildDetails();
    world.spawnTraffic();

    // If a shift was already started before NPC assets finished streaming,
    // pop the passenger(s) into existence now instead of waiting for the next fare.
    const snap = missionManager.snapshot();
    if (snap.state === 'awaiting_pickup' && snap.pickupTarget) {
      pickupNpc.show(snap.pickupTarget);
    } else if (snap.state === 'en_route' && snap.dropoffTarget) {
      dropoffNpc.show(snap.dropoffTarget);
    }

    const phase4 = getManifestByPhase(4);
    await loader.loadPhase(phase4, 4);
    world.buildForest();
  })();

  // Debug info
  if (location.hash === '#debug') {
    const debugEl = document.getElementById('debug-info');
    if (debugEl) {
      debugEl.style.display = 'block';
      setInterval(() => {
        const info = engine.renderer.info;
        const mSnap = missionManager.snapshot();
        debugEl.textContent = [
          `FPS: ${fps}`,
          `Draw calls: ${info.render.calls}`,
          `Triangles: ${info.render.triangles}`,
          `Textures: ${info.memory.textures}`,
          `Geometries: ${info.memory.geometries}`,
          `Colliders: ${world.collisionWorld.getBoxCount()}`,
          `Pos: ${charController.position.x.toFixed(1)}, ${charController.position.z.toFixed(1)}`,
          `Mode: ${input.mode}`,
          `Mission: ${mSnap.state}`,
          `Rides: ${mSnap.rides} • $${mSnap.earnings.toFixed(0)}`,
          mSnap.timeRemaining != null ? `Time left: ${mSnap.timeRemaining.toFixed(1)}s` : '',
        ].filter(Boolean).join('\n');
      }, 500);
    }
  }
}

main().catch(console.error);
